import { ReactNode, useCallback, useMemo, useRef } from 'react'

import { useEvents } from '../../shared/hooks/useEvents'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { useSelector } from 'react-redux'

import { Box, Flex } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { throttle } from 'lodash'

import { Box as DarkBox, Flex as DarkFlex, tokenPrefix } from '@hivewatch/dark'

import { Spinner } from '@/components/ui'
import { selectIncidentTraceLogDrawerState } from '@/redux/ui/uiSlice'

import { EventFiltersI } from '../../shared/types/traceLogTypes'
import {
  filterEventsForIncidentTab,
  getResolvedActionEventRefs,
  isHighlightedFromResolution,
  matchesResolvedActionEventRef,
} from '../../shared/utils/traceLogUtils'
import { EventItem } from './EventItem'
import { TRACE_LOG_PANEL_HEIGHT } from './TraceLogContent'

const onEndReachedThreshold = 0.95

interface EventFeedPropsI extends EventFiltersI {
  showOnlyIncidentRelatedEvents?: boolean
  /** Shown once loading finishes with no events. */
  emptyState?: ReactNode
}

export const EventFeed = ({
  incident,
  filters,
  showOnlyIncidentRelatedEvents = false,
  emptyState,
}: EventFeedPropsI) => {
  const ref = useRef<HTMLDivElement>(null)
  const { highlightedEvent } = useSelector(selectIncidentTraceLogDrawerState)
  const isRebrand = useFeatureFlag(FeatureFlag.incidentDrawerRebrand)
  const mode = useLightMode()

  // For the Incident tab (showOnlyIncidentRelatedEvents=true), don't center on
  // highlightedEvent - use incident.detectedAt so all related events are fetched.
  // For Device/Facility tabs, center on highlightedEvent if user clicked an EVENT_REF.
  const centerTimestamp = showOnlyIncidentRelatedEvents
    ? undefined
    : highlightedEvent?.detectedAt

  const { isLoading, events, getNewerEvents, getOlderEvents, hasOlder } =
    useEvents({
      incident,
      filters,
      centerTimestamp,
    })

  const onScroll = useCallback(
    throttle(async () => {
      if (ref?.current) {
        const { scrollTop, scrollHeight, clientHeight } = ref.current

        // Scrolled to the top
        if (scrollTop <= 0) {
          const hasMoreEvents = await getNewerEvents()
          if (hasMoreEvents) {
            ref?.current?.scrollTo({ top: 50, behavior: 'auto' })
          }
        }
        // Scrolled to the bottom
        const scrollPosition = scrollTop + clientHeight
        const endScrollThreshold = scrollHeight * onEndReachedThreshold
        if (scrollPosition >= endScrollThreshold && hasOlder) {
          await getOlderEvents()
        }
      }
    }, 200),
    [getNewerEvents]
  )

  const eventExternalIdMatchesIncident = (eventExternalId: string) =>
    incident?.eventExternalIds?.includes(eventExternalId) ?? false

  const resolvedActionEventRefs = useMemo(
    () => getResolvedActionEventRefs(incident?.actions),
    [incident?.actions]
  )

  const filteredEvents = useMemo(
    () =>
      filterEventsForIncidentTab(
        events,
        showOnlyIncidentRelatedEvents,
        incident?.eventExternalIds,
        resolvedActionEventRefs
      ),
    [
      events,
      showOnlyIncidentRelatedEvents,
      incident?.eventExternalIds,
      resolvedActionEventRefs,
    ]
  )

  // The first load has no lower date bound, so an empty result means there
  // are no events for this filter at all, not just none near the incident.
  const isEmpty = !isLoading && !filteredEvents?.length

  const eventItems = (
    <AnimatePresence>
      {filteredEvents?.map((e) => (
        <motion.div
          animate={{ x: 0, opacity: 1 }}
          exit={{ opacity: 0 }}
          initial={{ x: -200, opacity: 0 }}
          key={e.node.externalId}
        >
          <EventItem
            event={e.node}
            isCauseOfIncident={eventExternalIdMatchesIncident(
              e.node.externalId
            )}
            isFromResolvedAction={matchesResolvedActionEventRef(
              e.node,
              resolvedActionEventRefs
            )}
            isHighlightedFromResolution={isHighlightedFromResolution(
              e.node,
              highlightedEvent
            )}
            key={e.node?.externalId}
            shouldShowDeviceInfo={Boolean(filters?.facilityId)}
          />
        </motion.div>
      ))}
    </AnimatePresence>
  )

  if (isRebrand) {
    const prefix = tokenPrefix(mode)
    return (
      <DarkFlex
        direction='column'
        flex='1 1 0%'
        gap='8px'
        minH={0}
        onScroll={onScroll}
        overflowX='hidden'
        overflowY='auto'
        px='24px'
        py='16px'
        ref={ref}
      >
        {eventItems}
        {isEmpty && emptyState}
        {isLoading && (
          <DarkBox
            alignContent='center'
            bg={`${prefix}bg.card-elevated`}
            border='1px solid'
            borderColor={`${prefix}border.subtle`}
            height='80px'
            left='calc(50% - 40px)'
            pointerEvents='none'
            position='absolute'
            top='calc(50% - 40px)'
            width='80px'
          >
            <Spinner size='lg' />
          </DarkBox>
        )}
      </DarkFlex>
    )
  }

  return (
    <Flex
      borderTop='1px solid'
      borderTopColor='border.200'
      direction='column'
      gap='8px'
      height={TRACE_LOG_PANEL_HEIGHT}
      onScroll={onScroll}
      overflowX='hidden'
      overflowY='auto'
      paddingInline='24px'
      paddingTop='24px'
      ref={ref}
    >
      {eventItems}
      {isEmpty && emptyState}
      {isLoading && (
        <Box
          alignContent='center'
          backgroundColor='white'
          borderColor='border.200'
          borderRadius='10px'
          borderWidth='1px'
          height='80px'
          left='calc(50% - 40px)'
          pointerEvents='none'
          position='absolute'
          top='calc(50% - 40px)'
          width='80px'
        >
          <Spinner size='lg' />
        </Box>
      )}
    </Flex>
  )
}
